import Phaser from 'phaser';

/**
 * Minimal floating virtual joystick for touch control.
 *
 * Appears wherever the player's finger first lands on the left half of
 * the screen (or anywhere if the right half is unused). Drag to set
 * direction; release to deactivate. Exposes a normalized {x, y} vector
 * the InputController polls each frame.
 *
 * Desktop mouse input also works — useful for playtesting without a
 * touchscreen.
 */
const BASE_RADIUS = 80;
const NUB_RADIUS = 32;
const MAX_DRAG = 70;
const LEFT_HALF_ONLY = false; // full-screen thumb landing for now

export class TouchJoystick {
  private base: Phaser.GameObjects.Arc;
  private nub: Phaser.GameObjects.Arc;
  private active = false;
  private pointerId: number | null = null;
  private vec = { x: 0, y: 0 };
  private disposers: Array<() => void> = [];

  constructor(private readonly scene: Phaser.Scene) {
    this.base = scene.add
      .circle(0, 0, BASE_RADIUS, 0xffffff, 0.08)
      .setStrokeStyle(2, 0xffffff, 0.35)
      .setScrollFactor(0, 0)
      .setDepth(1500)
      .setVisible(false);

    this.nub = scene.add
      .circle(0, 0, NUB_RADIUS, 0x9cff9c, 0.45)
      .setStrokeStyle(1, 0x6cff6c, 0.7)
      .setScrollFactor(0, 0)
      .setDepth(1501)
      .setVisible(false);

    const onDown = (p: Phaser.Input.Pointer) => this.onDown(p);
    const onMove = (p: Phaser.Input.Pointer) => this.onMove(p);
    const onUp = (p: Phaser.Input.Pointer) => this.onUp(p);

    scene.input.on('pointerdown', onDown);
    scene.input.on('pointermove', onMove);
    scene.input.on('pointerup', onUp);
    scene.input.on('pointerupoutside', onUp);

    this.disposers.push(() => {
      scene.input.off('pointerdown', onDown);
      scene.input.off('pointermove', onMove);
      scene.input.off('pointerup', onUp);
      scene.input.off('pointerupoutside', onUp);
    });
  }

  /** Current vector (x, y in [-1, 1], magnitude 0..1). */
  get vector(): { x: number; y: number } {
    return this.vec;
  }

  get isActive(): boolean {
    return this.active;
  }

  dispose(): void {
    for (const d of this.disposers) d();
    this.disposers = [];
    this.base.destroy();
    this.nub.destroy();
  }

  private onDown(pointer: Phaser.Input.Pointer): void {
    if (this.active) return;
    const halfW = this.scene.scale.width / 2;
    if (LEFT_HALF_ONLY && pointer.x > halfW) return;

    this.active = true;
    this.pointerId = pointer.id;
    this.base.setPosition(pointer.x, pointer.y).setVisible(true);
    this.nub.setPosition(pointer.x, pointer.y).setVisible(true);
    this.vec = { x: 0, y: 0 };
  }

  private onMove(pointer: Phaser.Input.Pointer): void {
    if (!this.active || pointer.id !== this.pointerId) return;
    const dx = pointer.x - this.base.x;
    const dy = pointer.y - this.base.y;
    const dist = Math.hypot(dx, dy);
    const clamped = Math.min(MAX_DRAG, dist);
    const nx = dist < 1e-6 ? 0 : (dx / dist) * clamped;
    const ny = dist < 1e-6 ? 0 : (dy / dist) * clamped;
    this.nub.setPosition(this.base.x + nx, this.base.y + ny);
    this.vec = { x: nx / MAX_DRAG, y: ny / MAX_DRAG };
  }

  private onUp(pointer: Phaser.Input.Pointer): void {
    if (pointer.id !== this.pointerId) return;
    this.active = false;
    this.pointerId = null;
    this.vec = { x: 0, y: 0 };
    this.base.setVisible(false);
    this.nub.setVisible(false);
  }
}
